import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from '../api/axios';
import { toast } from 'react-toastify';
import { motion } from 'framer-motion';
import { DashboardSkeleton } from '../components/Skeleton';
import { useAuth } from '../context/AuthContext';
import { fadeUp, staggerContainer, cardItem } from '../lib/motion';

// Maps backend status values to a label + color treatment for the badge.
const STATUS_STYLES = {
  created: { label: 'Received', className: 'bg-gray-100 text-gray-600' },
  assigned: { label: 'Assigned', className: 'bg-blue-50 text-blue-700' },
  in_progress: { label: 'In progress', className: 'bg-amber-50 text-amber-700' },
  completed: { label: 'Completed', className: 'bg-green-50 text-green-700' },
  cancelled: { label: 'Cancelled', className: 'bg-red-50 text-red-700' },
};

const OrderCard = ({ order, onCancel }) => {
  const statusInfo = STATUS_STYLES[order.status] || STATUS_STYLES.created;
  const date = order.createdAt
    ? new Date(order.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
    : '';

  const canCancel = order.status === 'created' || order.status === 'assigned';

  return (
    <motion.div
      variants={cardItem}
      className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm flex flex-col justify-between"
    >
      <div>
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <p className="text-xs text-gray-400 font-mono">{order.orderNumber || order._id}</p>
            <h3 className="text-sm font-semibold text-gray-900 mt-0.5">
              {order.service?.printerModel || 'Service request'}
            </h3>
          </div>
          <span className={`text-xs font-medium px-2.5 py-1 rounded-full whitespace-nowrap ${statusInfo.className}`}>
            {statusInfo.label}
          </span>
        </div>

        <p className="text-sm text-gray-500 leading-relaxed mb-3 line-clamp-2">
          {order.service?.description}
        </p>

        {order.engineer?.name && (
          <p className="text-xs text-gray-500 mt-2 mb-3">
            Engineer: <span className="text-gray-700 font-medium">{order.engineer.name}</span>
          </p>
        )}
      </div>

      <div>
        <div className="flex items-center justify-between text-xs text-gray-400 pt-3 border-t border-gray-50">
          <span>{order.service?.doorstep ? '🏠 Doorstep' : '🏪 In-shop'}</span>
          <span>{date}</span>
        </div>

        {canCancel && (
          <button
            onClick={() => onCancel(order._id, order.orderNumber || order._id)}
            className="w-full mt-3 bg-red-50 hover:bg-red-100 text-red-600 text-xs font-semibold py-2 rounded-lg transition-colors"
          >
            Cancel Request
          </button>
        )}
      </div>
    </motion.div>
  );
};

const CustomerDashboard = () => {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMyOrders(true);

    // Poll every 30 seconds for status updates silently
    const interval = setInterval(() => {
      fetchMyOrders(false);
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const fetchMyOrders = async (showLoading = true) => {
    if (showLoading) setLoading(true);
    try {
      const response = await axios.get('/orders/my-orders');
      setOrders(response.data?.data || []);
    } catch (error) {
      toast.error('Failed to load your orders');
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  const handleCancelOrder = async (orderId, orderNum) => {
    if (!window.confirm(`Are you sure you want to cancel service request #${orderNum}?`)) {
      return;
    }
    try {
      const response = await axios.put(`/orders/${orderId}/cancel-customer`);
      if (response.data.success) {
        toast.success('Service request cancelled successfully.');
        fetchMyOrders(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to cancel service request.');
    }
  };

  const activeOrders = orders.filter((o) => o.status !== 'completed' && o.status !== 'cancelled');
  const pastOrders = orders.filter((o) => o.status === 'completed' || o.status === 'cancelled');

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-6 py-10">

        {/* Header */}
        <motion.div
          className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8"
          initial="hidden"
          animate="show"
          variants={fadeUp}
        >
          <div>
            <p className="text-xs font-semibold text-blue-600 uppercase tracking-widest mb-1">Dashboard</p>
            <h1 className="text-2xl font-bold text-gray-900">Welcome back, {user?.name?.split(' ')[0] || 'there'}</h1>
          </div>
          <Link
            to="/book-service"
            className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-5 py-2.5 rounded-xl transition-colors text-center"
          >
            + Book a new service
          </Link>
        </motion.div>

        {loading ? (
          <DashboardSkeleton />
        ) : orders.length === 0 ? (
          // Empty state — no orders yet
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white border border-gray-100 rounded-xl p-10 text-center"
          >
            <p className="text-3xl mb-3">🖨️</p>
            <p className="text-gray-700 font-medium mb-1">No service requests yet</p>
            <p className="text-gray-400 text-sm mb-5">Once you book a service, you'll be able to track it here.</p>
            <Link
              to="/book-service"
              className="inline-block bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-6 py-2.5 rounded-xl transition-colors"
            >
              Book your first service
            </Link>
          </motion.div>
        ) : (
          <>
            {/* Active orders */}
            {activeOrders.length > 0 && (
              <div className="mb-10">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">
                  Active ({activeOrders.length})
                </p>
                <motion.div
                  className="grid grid-cols-1 sm:grid-cols-2 gap-4"
                  variants={staggerContainer}
                  initial="hidden"
                  animate="show"
                >
                  {activeOrders.map((order) => (
                    <OrderCard key={order._id} order={order} onCancel={handleCancelOrder} />
                  ))}
                </motion.div>
              </div>
            )}

            {/* Past orders */}
            {pastOrders.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">
                  Past ({pastOrders.length})
                </p>
                <motion.div
                  className="grid grid-cols-1 sm:grid-cols-2 gap-4"
                  variants={staggerContainer}
                  initial="hidden"
                  animate="show"
                >
                  {pastOrders.map((order) => (
                    <OrderCard key={order._id} order={order} onCancel={handleCancelOrder} />
                  ))}
                </motion.div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CustomerDashboard;